import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FileText, Plus, AlertTriangle, Trash2, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [r, u] = await Promise.all([api.get("/reports"), api.get("/usage")]);
      setReports(r.data); setUsage(u.data);
    } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this report?")) return;
    try { await api.delete(`/reports/${id}`); setReports(reports.filter(r => r.id !== id)); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const pct = usage && usage.limit ? Math.min(100, (usage.usage_this_month / usage.limit) * 100) : 0;
  const overLimit = usage && usage.plan === "free" && !usage.can_analyze;

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 py-10">
        {/* HEADER */}
        <div className="grid lg:grid-cols-12 gap-6 mb-10">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">/ Dashboard · {user?.email}</div>
            <h1 className="font-display font-black text-5xl tracking-tighter leading-none">Your Reports</h1>
            <p className="text-sm text-zinc-600 mt-4 max-w-xl">Upload a PDF or fetch by ticker. FinLens will surface the red flags hiding in the fine print.</p>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-0 border-2 border-strong bg-white">
            <div className="p-5 border-r-2 border-strong">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Plan</div>
              <div className="font-display font-black text-2xl tracking-tighter">{usage?.plan?.toUpperCase() || "—"}</div>
              {usage?.plan === "free" && <div className="text-xs text-zinc-500 mt-1">{usage.usage_this_month}/{usage.limit} used</div>}
              {usage?.plan === "owner" && <div className="text-xs text-[#FF2A00] mt-1">Owner · Unlimited</div>}
            </div>
            <div className="p-5 flex flex-col justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">This Month</div>
                <div className="font-display font-black text-2xl tracking-tighter">{usage?.usage_this_month ?? 0}</div>
              </div>
              {usage?.plan === "free" && (
                <div className="mt-3">
                  <div className="h-1.5 bg-zinc-200 w-full">
                    <div className="h-1.5 bg-black" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="grid md:grid-cols-3 gap-0 border-2 border-strong bg-white mb-10">
          <Link to="/app/analyze" className="p-6 border-r-0 md:border-r-2 border-strong hover:bg-[#F4F4F5] transition-colors flex items-center justify-between" data-testid="dashboard-new-analysis">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">/ New</div>
              <div className="font-display font-bold text-xl tracking-tight">Analyze Report</div>
              <div className="text-xs text-zinc-500 mt-1">PDF or ticker</div>
            </div>
            <Plus className="w-8 h-8" strokeWidth={1.5} />
          </Link>
          <div className="p-6 border-r-0 md:border-r-2 border-strong">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">/ Reports</div>
            <div className="font-display font-bold text-xl tracking-tight">{reports.length}</div>
            <div className="text-xs text-zinc-500 mt-1">Total in archive</div>
          </div>
          <div className="p-6 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">/ Red Flags Found</div>
              <div className="font-display font-bold text-xl tracking-tight">{reports.reduce((acc, r) => acc + (r.analysis?.red_flags?.length || 0), 0)}</div>
            </div>
            <AlertTriangle className="w-8 h-8 text-[#FF2A00]" strokeWidth={1.5} />
          </div>
        </div>

        {overLimit && (
          <div className="border-l-4 border-[#FF2A00] bg-[#FEF2F2] p-5 mb-8 flex items-center justify-between" data-testid="dashboard-over-limit">
            <div>
              <div className="font-bold">You've used your {usage?.limit ?? ""} free reports this month.</div>
              <div className="text-sm text-zinc-700">Your limit resets at the start of next month.</div>
            </div>
          </div>
        )}

        {/* REPORTS LIST */}
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">/ Archive</div>
        {loading ? (
          <div className="text-sm text-zinc-500">Loading…</div>
        ) : reports.length === 0 ? (
          <div className="border-2 border-dashed border-strong bg-white p-16 text-center">
            <FileText className="w-10 h-10 mx-auto mb-4 text-zinc-400" />
            <div className="font-display font-bold text-2xl tracking-tight mb-2">No reports yet</div>
            <div className="text-sm text-zinc-500 mb-6">Upload an annual or quarterly report to begin.</div>
            <Link to="/app/analyze" className="inline-block bg-black text-white px-6 py-3 text-xs uppercase tracking-[0.2em] hover:bg-[#002FA7]" data-testid="dashboard-empty-cta">Analyze First Report</Link>
          </div>
        ) : (
          <div className="border-2 border-strong bg-white">
            <div className="grid grid-cols-12 text-[10px] uppercase tracking-[0.2em] text-zinc-500 px-5 py-3 border-b-2 border-strong bg-zinc-100">
              <div className="col-span-5">Title</div>
              <div className="col-span-2">Source</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2">Flags</div>
              <div className="col-span-1 text-right">—</div>
            </div>
            {reports.map((r) => (
              <Link
                to={`/app/report/${r.id}`}
                key={r.id}
                data-testid={`report-row-${r.id}`}
                className="grid grid-cols-12 items-center px-5 py-4 border-b border-zinc-200 hover:bg-zinc-50 text-sm"
              >
                <div className="col-span-5 font-medium truncate">{r.title}</div>
                <div className="col-span-2 text-xs uppercase tracking-wider text-zinc-500">{r.source === "pdf_upload" ? "PDF" : "TICKER"}</div>
                <div className="col-span-2">
                  {r.analyzed ? (
                    <span className="inline-flex items-center gap-1 text-xs"><CheckCircle2 className="w-3.5 h-3.5 text-[#00C853]" /> Analyzed</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-zinc-500"><Clock className="w-3.5 h-3.5" /> Pending</span>
                  )}
                </div>
                <div className="col-span-2">
                  {r.analyzed && r.analysis?.red_flags?.length > 0 ? (
                    <span className="bg-[#FEF2F2] text-[#FF2A00] border border-[#FF2A00] px-2 py-0.5 text-xs font-bold">{r.analysis.red_flags.length}</span>
                  ) : r.analyzed ? <span className="text-xs text-zinc-400">none</span> : <span className="text-xs text-zinc-400">—</span>}
                </div>
                <div className="col-span-1 text-right">
                  <button
                    onClick={(e) => { e.preventDefault(); del(r.id); }}
                    data-testid={`delete-report-${r.id}`}
                    className="text-zinc-400 hover:text-[#FF2A00]"
                  ><Trash2 className="w-4 h-4" /></button>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
